/**
 * VCard.js — Premium vCard builder with:
 *   • 8 template presets with live visual previews
 *   • 8 font families
 *   • Background types: gradient / solid color / image URL
 *   • Color theme picker (8 accent colors)
 *   • Button style selector
 *   • Live mini-preview that updates as you type
 *   • All existing JJuma logic preserved (API calls, links CRUD, status toggle)
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import {
  AlertCircle, AtSign, CheckCircle2, Copy, Edit3,
  ExternalLink, Eye, Globe, IdCard, Link2, MapPin,
  Palette, Phone, Plus, Share2, Trash2, User
} from 'lucide-react';
import { toast } from 'sonner';
import { getLinkUrl } from '../../lib/domains';

/* ─── design constants ────────────────────────────────────── */
export const THEME_ACCENT = {
  green:  '#047857', blue:   '#1d4ed8', purple: '#7c3aed',
  gold:   '#b45309', dark:   '#1e293b', rose:   '#be185d',
  teal:   '#0d9488', orange: '#ea580c',
};

export const FONT_MAP = {
  outfit:     "'Outfit', sans-serif",
  poppins:    "'Poppins', sans-serif",
  inter:      "'Inter', sans-serif",
  playfair:   "'Playfair Display', serif",
  montserrat: "'Montserrat', sans-serif",
  raleway:    "'Raleway', sans-serif",
  lora:       "'Lora', serif",
  dancing:    "'Dancing Script', cursive",
};

const TEMPLATES = [
  {
    key: 'modern',
    label: 'Modern',
    desc: 'Gradient cover, clean card',
    accent: '#047857',
    dark: false,
    preview: {
      cover: 'linear-gradient(135deg,#047857,#10b981)',
      card:  '#ffffff',
      text:  '#0f172a',
    },
  },
  {
    key: 'musician',
    label: 'Musician',
    desc: 'Dark stage, neon glow',
    accent: '#7c3aed',
    dark: true,
    preview: {
      cover: 'linear-gradient(135deg,#1a1a2e,#16213e)',
      card:  '#1a1a2e',
      text:  '#ffffff',
    },
  },
  {
    key: 'minimal',
    label: 'Minimal',
    desc: 'Clean, left-aligned, type-first',
    accent: '#1d4ed8',
    dark: false,
    preview: {
      cover: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
      card:  '#ffffff',
      text:  '#0f172a',
    },
  },
  {
    key: 'bold',
    label: 'Bold',
    desc: 'Strong color top, white card',
    accent: '#0f172a',
    dark: true,
    preview: {
      cover: 'linear-gradient(135deg,#0f172a,#334155)',
      card:  '#ffffff',
      text:  '#0f172a',
    },
  },
  {
    key: 'luxury',
    label: 'Luxury',
    desc: 'Black & gold, serif elegance',
    accent: '#c9a227',
    dark: true,
    preview: {
      cover: 'linear-gradient(135deg,#0d0d0d,#1a1500)',
      card:  '#0d0d0d',
      text:  '#c9a227',
    },
  },
  {
    key: 'creative',
    label: 'Creative',
    desc: 'Vivid gradient, glassmorphism',
    accent: '#7c3aed',
    dark: true,
    preview: {
      cover: 'linear-gradient(135deg,#7c3aed,#4f46e5)',
      card:  'rgba(255,255,255,0.12)',
      text:  '#ffffff',
    },
  },
  {
    key: 'nature',
    label: 'Nature',
    desc: 'Earthy, warm, organic',
    accent: '#5c7a3e',
    dark: false,
    preview: {
      cover: 'linear-gradient(135deg,#fdf6ed,#f0e6d3)',
      card:  '#fff8f0',
      text:  '#2d2015',
    },
  },
  {
    key: 'corporate',
    label: 'Corporate',
    desc: 'Professional, grid layout',
    accent: '#1d4ed8',
    dark: false,
    preview: {
      cover: 'linear-gradient(135deg,#1d4ed8,#3b82f6)',
      card:  '#ffffff',
      text:  '#0f172a',
    },
  },
];

const FONTS = [
  { key: 'outfit',     label: 'Outfit',           sample: 'Modern & Clean' },
  { key: 'poppins',    label: 'Poppins',          sample: 'Friendly Rounded' },
  { key: 'inter',      label: 'Inter',            sample: 'Tech Minimal' },
  { key: 'playfair',   label: 'Playfair Display', sample: 'Elegant Serif' },
  { key: 'montserrat', label: 'Montserrat',       sample: 'Bold & Strong' },
  { key: 'raleway',    label: 'Raleway',          sample: 'Refined Thin' },
  { key: 'lora',       label: 'Lora',             sample: 'Editorial Warm' },
  { key: 'dancing',    label: 'Dancing Script',   sample: 'Creative Script' },
];

const ACCENT_COLORS = [
  { key: 'green',  hex: '#047857', label: 'Emerald' },
  { key: 'blue',   hex: '#1d4ed8', label: 'Blue' },
  { key: 'purple', hex: '#7c3aed', label: 'Purple' },
  { key: 'gold',   hex: '#b45309', label: 'Gold' },
  { key: 'dark',   hex: '#1e293b', label: 'Dark' },
  { key: 'rose',   hex: '#be185d', label: 'Rose' },
  { key: 'teal',   hex: '#0d9488', label: 'Teal' },
  { key: 'orange', hex: '#ea580c', label: 'Orange' },
];

const BG_PRESETS = [
  { label: 'Auto gradient', value: '', type: 'gradient' },
  { label: 'Soft slate',    value: '#f1f5f9', type: 'solid' },
  { label: 'Warm cream',    value: '#fdf6ed', type: 'solid' },
  { label: 'Midnight',      value: '#0a0a0f', type: 'solid' },
  { label: 'Deep navy',     value: '#0f172a', type: 'solid' },
  { label: 'Rose blush',    value: '#fff1f2', type: 'solid' },
];

const TABS = [
  { key: 'profile',  label: 'Profile',  icon: User },
  { key: 'contact',  label: 'Contact',  icon: Phone },
  { key: 'social',   label: 'Social',   icon: Share2 },
  { key: 'links',    label: 'Links',    icon: Link2 },
  { key: 'design',   label: 'Design',   icon: Palette },
  { key: 'seo',      label: 'SEO',      icon: Globe },
];

const EMPTY_VCARD = {
  display_name: '', brand_name: '', username: '', job_title: '', profile_image: '',
  cover_image: '', bio: '', phone: '', whatsapp: '', email: '', website: '',
  location: '', payment_link_id: '', seo_title: '', seo_description: '',
  seo_keywords: '', social_share_image: '', show_jjuma_branding: true,
  facebook: '', instagram: '', tiktok: '', youtube: '', x_twitter: '',
  linkedin: '', status: 'active',
  /* design fields */
  template: 'modern', font_family: 'outfit', theme_color: 'green',
  bg_type: 'gradient', bg_value: '', button_style: 'rounded',
};
const EMPTY_LINK = { title: '', url: '', icon: '', sort_order: 0, is_active: true };

function formatLinks(input) {
  if (!Array.isArray(input)) return [];
  return input.filter(Boolean).map((link, idx) => ({
    id: link.id || `link-${idx + 1}`,
    title: link.title || '', url: link.url || '', icon: link.icon || '',
    sort_order: Number.isFinite(Number(link.sort_order)) ? Number(link.sort_order) : idx + 1,
    is_active: link.is_active !== false,
  })).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
}

function buildPublicUrl(username = '') {
  if (!username) return '';
  return getLinkUrl(`/${username}`);
}

/* ─── mini template thumbnail ─────────────────────────────── */
function TemplateThumbnail({ tpl, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative rounded-2xl overflow-hidden border-2 transition-all duration-200 text-left ${
        selected
          ? 'border-emerald-600 shadow-lg scale-[1.02] ring-2 ring-emerald-200'
          : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
      }`}
      style={{ aspectRatio: '9/16', background: tpl.preview.cover }}
    >
      {/* Simulated phone layout inside thumbnail */}
      <div className="absolute inset-0 flex flex-col">
        {/* Cover area */}
        <div className="flex-[2] relative" style={{ background: tpl.preview.cover }}>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full border-2 border-white bg-white/30 shadow" />
        </div>
        {/* Card area */}
        <div className="flex-[3] p-2 space-y-1" style={{ background: tpl.preview.card }}>
          <div className="h-2 rounded w-16 mx-auto" style={{ background: `${tpl.preview.text}40` }} />
          <div className="h-1.5 rounded w-10 mx-auto" style={{ background: `${tpl.accent}80` }} />
          <div className="grid grid-cols-2 gap-1 mt-1">
            {[0,1,2,3].map(i=>(
              <div key={i} className="h-3 rounded" style={{ background: tpl.preview.text === '#ffffff' ? 'rgba(255,255,255,0.15)' : '#f1f5f9' }} />
            ))}
          </div>
        </div>
      </div>
      {/* Selected badge */}
      {selected && (
        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center shadow">
          <CheckCircle2 size={12} className="text-white" />
        </div>
      )}
      {/* Label */}
      <div className="absolute bottom-0 inset-x-0 px-2 py-1.5 text-center"
           style={{ background: tpl.dark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.85)', backdropFilter: 'blur(4px)' }}>
        <p className="text-[10px] font-bold truncate" style={{ color: tpl.dark ? '#fff' : '#0f172a' }}>{tpl.label}</p>
        <p className="text-[8px] truncate" style={{ color: tpl.dark ? 'rgba(255,255,255,0.6)' : '#64748b' }}>{tpl.desc}</p>
      </div>
    </button>
  );
}

/* ─── live mini vCard preview ─────────────────────────────── */
function LivePreview({ form }) {
  const accent  = THEME_ACCENT[form.theme_color] || THEME_ACCENT.green;
  const font    = FONT_MAP[form.font_family]     || FONT_MAP.outfit;
  const tpl     = TEMPLATES.find(t => t.key === form.template) || TEMPLATES[0];
  const name    = form.display_name || 'Your Name';
  const title   = form.job_title || form.brand_name || '';
  const bio     = form.bio || '';
  const avatar  = form.profile_image;
  const cover   = form.cover_image;
  const bgStyle = form.bg_type === 'image' && form.bg_value
    ? { backgroundImage: `url(${form.bg_value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : form.bg_type === 'solid' && form.bg_value
      ? { background: form.bg_value }
      : { background: `linear-gradient(160deg, ${accent}22 0%, #f1f5f9 100%)` };

  return (
    <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-lg" style={{ fontFamily: font, ...bgStyle }}>
      {/* Cover */}
      <div className="h-24 relative" style={cover ? undefined : { background: tpl.dark ? tpl.preview.cover : `linear-gradient(135deg,${accent},${accent}99)` }}>
        {cover && <img src={cover} alt="" className="h-full w-full object-cover" />}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.2))' }} />
      </div>
      {/* Content */}
      <div className="px-4 pb-4" style={{ background: tpl.dark ? tpl.preview.card : '#ffffff' }}>
        <div className="relative -mt-8 mb-2 w-14 h-14 rounded-xl overflow-hidden border-[3px] border-white shadow-lg bg-white">
          {avatar
            ? <img src={avatar} alt="" className="h-full w-full object-cover" />
            : <div className="h-full w-full flex items-center justify-center text-xl font-bold" style={{ color: accent, background: `${accent}18` }}>{name.charAt(0).toUpperCase()}</div>}
        </div>
        <p className="text-sm font-bold truncate" style={{ color: tpl.dark ? '#ffffff' : '#0f172a' }}>{name}</p>
        {title && <p className="text-xs font-semibold truncate mt-0.5" style={{ color: accent }}>{title}</p>}
        {bio && <p className="text-xs mt-1.5 line-clamp-2 leading-relaxed" style={{ color: tpl.dark ? 'rgba(255,255,255,0.6)' : '#64748b' }}>{bio}</p>}
        <div className="mt-3 grid grid-cols-2 gap-1.5">
          {['WhatsApp','Call','Email','Website'].map(lbl => (
            <div key={lbl} className="rounded-lg py-1.5 text-center text-[9px] font-bold"
                 style={{ background: lbl === 'WhatsApp' ? '#25D366' : lbl === 'Call' ? accent : lbl === 'Email' ? '#ef4444' : (tpl.dark ? 'rgba(255,255,255,0.1)' : '#f1f5f9'), color: lbl === 'Website' ? (tpl.dark ? '#fff' : '#374151') : '#fff' }}>
              {lbl}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Field helpers ───────────────────────────────────────── */
function Field({ label, helper, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      {children}
      {helper && <p className="text-xs text-slate-400 mt-1">{helper}</p>}
    </div>
  );
}
function FInput({ label, helper, value, onChange, placeholder, type = 'text', prefix }) {
  return (
    <Field label={label} helper={helper}>
      {prefix ? (
        <div className="flex mt-1 rounded-xl border border-slate-200 overflow-hidden focus-within:ring-2 focus-within:ring-emerald-500 focus-within:border-emerald-500">
          <span className="flex items-center px-3 bg-slate-50 text-slate-500 text-sm border-r border-slate-200 shrink-0">{prefix}</span>
          <input type={type} value={value||''} onChange={onChange} placeholder={placeholder} className="flex-1 px-3 py-2 text-sm outline-none bg-white" />
        </div>
      ) : (
        <Input type={type} value={value||''} onChange={onChange} placeholder={placeholder} className="mt-1 rounded-xl border-slate-200 text-sm" />
      )}
    </Field>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════ */
export default function VCardPage() {
  const [data,          setData]          = useState(null);
  const [form,          setForm]          = useState(EMPTY_VCARD);
  const [linkForm,      setLinkForm]      = useState(EMPTY_LINK);
  const [editingLinkId, setEditingLinkId] = useState('');
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [linkSaving,    setLinkSaving]    = useState(false);
  const [activeTab,     setActiveTab]     = useState('profile');

  const currentVcard = data?.vcard || null;
  const links        = useMemo(() => formatLinks(currentVcard?.links || currentVcard?.custom_links || []), [currentVcard]);
  const publicUrl    = useMemo(() => buildPublicUrl(form.username || currentVcard?.username || ''), [form.username, currentVcard?.username]);

  useEffect(() => {
    api.get('/dashboard/vcard')
      .then(r => {
        setData(r.data);
        if (r.data.vcard) {
          const v = r.data.vcard;
          setForm({
            display_name:       v.display_name || v.full_name || '',
            brand_name:         v.brand_name || '',
            username:           v.username || '',
            job_title:          v.job_title || '',
            profile_image:      v.profile_image || v.profile_photo_url || '',
            cover_image:        v.cover_image || v.cover_image_url || '',
            bio:                v.bio || '',
            phone:              v.phone || '',
            whatsapp:           v.whatsapp || '',
            email:              v.email || '',
            website:            v.website || '',
            location:           v.location || '',
            payment_link_id:    v.payment_link_id || v.payment_link || '',
            seo_title:          v.seo_title || '',
            seo_description:    v.seo_description || '',
            seo_keywords:       Array.isArray(v.seo_keywords) ? v.seo_keywords.join(', ') : (v.seo_keywords || ''),
            social_share_image: v.social_share_image || v.social_sharing_image_url || '',
            show_jjuma_branding:v.show_jjuma_branding !== false,
            facebook:           v.facebook || v.social_links?.facebook || '',
            instagram:          v.instagram || v.social_links?.instagram || '',
            tiktok:             v.tiktok   || v.social_links?.tiktok   || '',
            youtube:            v.youtube  || v.social_links?.youtube  || '',
            x_twitter:          v.x_twitter|| v.social_links?.x        || '',
            linkedin:           v.linkedin || v.social_links?.linkedin || '',
            status:             v.status   || 'active',
            template:           v.template    || v.design?.template    || 'modern',
            font_family:        v.font_family || v.design?.font_family || 'outfit',
            theme_color:        v.theme_color || v.design?.theme_color || 'green',
            bg_type:            v.bg_type     || v.design?.bg_type     || 'gradient',
            bg_value:           v.bg_value    || v.design?.bg_value    || '',
            button_style:       v.button_style|| v.design?.button_style|| 'rounded',
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const saveVcard = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        seo_keywords: String(form.seo_keywords || '').split(',').map(v => v.trim()).filter(Boolean),
        social_links: {
          facebook: form.facebook, instagram: form.instagram, tiktok: form.tiktok,
          youtube:  form.youtube,  x: form.x_twitter,         linkedin: form.linkedin,
        },
        design: {
          template:     form.template,
          font_family:  form.font_family,
          theme_color:  form.theme_color,
          bg_type:      form.bg_type,
          bg_value:     form.bg_value,
          button_style: form.button_style,
        },
      };
      const res = currentVcard?.id
        ? await api.put(`/dashboard/vcard/${currentVcard.id}`, payload)
        : await api.post('/dashboard/vcard', payload);
      setData(prev => ({ ...prev, vcard: res.data }));
      toast.success('vCard saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save vCard');
    } finally { setSaving(false); }
  };

  const saveLink = async () => {
    if (!currentVcard?.id) { toast.error('Save your vCard first before adding links'); return; }
    if (!linkForm.title.trim() || !linkForm.url.trim()) { toast.error('Link title and URL are required'); return; }
    setLinkSaving(true);
    try {
      const payload = { title: linkForm.title.trim(), url: linkForm.url.trim(), icon: linkForm.icon.trim(), sort_order: Number(linkForm.sort_order || 0), is_active: !!linkForm.is_active };
      const response = editingLinkId
        ? await api.put(`/dashboard/vcard/${currentVcard.id}/links/${editingLinkId}`, payload)
        : await api.post(`/dashboard/vcard/${currentVcard.id}/links`, payload);
      const nextLinks = editingLinkId
        ? links.map(item => item.id === editingLinkId ? response.data.link : item)
        : [...links, response.data.link];
      setData(prev => ({ ...prev, vcard: { ...prev.vcard, links: nextLinks, custom_links: nextLinks } }));
      setLinkForm(EMPTY_LINK); setEditingLinkId('');
      toast.success(editingLinkId ? 'Link updated' : 'Link added');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save link');
    } finally { setLinkSaving(false); }
  };

  const editLink   = (link) => { setEditingLinkId(link.id); setLinkForm({ title: link.title||'', url: link.url||'', icon: link.icon||'', sort_order: link.sort_order||0, is_active: link.is_active!==false }); };
  const deleteLink = async (linkId) => {
    if (!currentVcard?.id) return;
    try {
      await api.delete(`/dashboard/vcard/${currentVcard.id}/links/${linkId}`);
      const nextLinks = links.filter(item => item.id !== linkId);
      setData(prev => ({ ...prev, vcard: { ...prev.vcard, links: nextLinks, custom_links: nextLinks } }));
      toast.success('Link deleted');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const toggleStatus = async (status) => {
    if (!currentVcard?.id) return;
    try {
      const { data: res } = await api.patch(`/dashboard/vcard/${currentVcard.id}/status`, { status });
      setData(prev => ({ ...prev, vcard: res.vcard }));
      set('status', status);
      toast.success(`vCard ${status === 'active' ? 'enabled' : 'disabled'}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
  };

  const copyShareLink = async () => {
    if (!publicUrl) return;
    try { await navigator.clipboard.writeText(publicUrl); toast.success('Link copied!'); } catch { toast.error('Could not copy'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-48">
      <div className="w-7 h-7 border-[3px] border-emerald-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  /* ── Tab panels ─────────────────────────────────────────── */
  const renderTab = () => {
    switch (activeTab) {

      /* ── PROFILE ──────────────────────────── */
      case 'profile': return (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <FInput label="Display Name"        value={form.display_name} onChange={e=>set('display_name',e.target.value)} placeholder="Jane Doe" />
            <FInput label="Brand / Business"    value={form.brand_name}   onChange={e=>set('brand_name',  e.target.value)} placeholder="My Business" />
            <FInput label="Job Title / Role"    value={form.job_title}    onChange={e=>set('job_title',   e.target.value)} placeholder="Founder & CEO" />
            <FInput label="Username" helper="link.jjuma.com/username"
              prefix="@" value={form.username}
              onChange={e=>set('username', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g,''))}
              placeholder="yourname" />
          </div>
          <Field label="Bio">
            <Textarea value={form.bio} onChange={e=>set('bio',e.target.value)} className="mt-1 rounded-xl border-slate-200 text-sm resize-none" rows={3} placeholder="Tell visitors about yourself…" />
          </Field>
          <div className="grid gap-4 md:grid-cols-2">
            <FInput label="Profile Image URL"  value={form.profile_image} onChange={e=>set('profile_image',e.target.value)} placeholder="https://…" helper="Photo of yourself or your brand" />
            <FInput label="Cover / Banner URL" value={form.cover_image}   onChange={e=>set('cover_image',  e.target.value)} placeholder="https://…" helper="Background cover image" />
          </div>
          {/* Image previews */}
          {(form.profile_image || form.cover_image) && (
            <div className="grid grid-cols-2 gap-3">
              {form.profile_image && <div className="space-y-1"><p className="text-xs text-slate-400">Avatar preview</p><img src={form.profile_image} alt="avatar" className="h-16 w-16 rounded-xl object-cover border border-slate-200" onError={e=>{e.target.style.display='none'}} /></div>}
              {form.cover_image   && <div className="space-y-1"><p className="text-xs text-slate-400">Cover preview</p><img src={form.cover_image} alt="cover" className="h-16 rounded-xl object-cover border border-slate-200 w-full" onError={e=>{e.target.style.display='none'}} /></div>}
            </div>
          )}
        </div>
      );

      /* ── CONTACT ──────────────────────────── */
      case 'contact': return (
        <div className="grid gap-4 md:grid-cols-2">
          <FInput label="Phone"     value={form.phone}    onChange={e=>set('phone',   e.target.value)} placeholder="+256700000000" />
          <FInput label="WhatsApp"  value={form.whatsapp} onChange={e=>set('whatsapp',e.target.value)} placeholder="+256700000000" />
          <FInput label="Email" type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="you@example.com" />
          <FInput label="Website"   value={form.website}  onChange={e=>set('website', e.target.value)} placeholder="https://yoursite.com" />
          <Field label="Location">
            <div className="flex mt-1 rounded-xl border border-slate-200 overflow-hidden focus-within:ring-2 focus-within:ring-emerald-500">
              <span className="flex items-center px-3 bg-slate-50 border-r border-slate-200"><MapPin size={13} className="text-slate-400"/></span>
              <input value={form.location} onChange={e=>set('location',e.target.value)} placeholder="Kampala, Uganda" className="flex-1 px-3 py-2 text-sm outline-none bg-white" />
            </div>
          </Field>
          <FInput label="Payment / Donation Link" helper="CTA button on your public page" value={form.payment_link_id} onChange={e=>set('payment_link_id',e.target.value)} placeholder="/pay/your-link" />
        </div>
      );

      /* ── SOCIAL ───────────────────────────── */
      case 'social': return (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Paste full URLs. Blank fields are hidden on your public page.</p>
          {[
            { emoji:'📘', label:'Facebook',  field:'facebook',  ph:'https://facebook.com/…'  },
            { emoji:'📸', label:'Instagram', field:'instagram', ph:'https://instagram.com/…' },
            { emoji:'🎵', label:'TikTok',    field:'tiktok',    ph:'https://tiktok.com/@…'   },
            { emoji:'▶️', label:'YouTube',   field:'youtube',   ph:'https://youtube.com/@…'  },
            { emoji:'𝕏',  label:'X/Twitter', field:'x_twitter', ph:'https://x.com/…'         },
            { emoji:'💼', label:'LinkedIn',  field:'linkedin',  ph:'https://linkedin.com/in/…'},
          ].map(({ emoji, label, field, ph }) => (
            <div key={field} className="flex items-center gap-3">
              <span className="text-xl w-7 text-center shrink-0">{emoji}</span>
              <div className="flex-1">
                <Input value={form[field]||''} onChange={e=>set(field,e.target.value)} placeholder={ph} className="rounded-xl border-slate-200 text-sm" />
              </div>
            </div>
          ))}
        </div>
      );

      /* ── LINKS ────────────────────────────── */
      case 'links': return (
        <div className="space-y-4">
          {currentVcard?.id ? (
            <>
              {/* Link form */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                <p className="text-sm font-bold text-slate-900">{editingLinkId ? '✏️ Edit Link' : '＋ Add New Link'}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Title"><Input value={linkForm.title} onChange={e=>setLinkForm(p=>({...p,title:e.target.value}))} className="mt-1 rounded-xl border-slate-200 text-sm" placeholder="My Portfolio" /></Field>
                  <Field label="URL"><Input value={linkForm.url} onChange={e=>setLinkForm(p=>({...p,url:e.target.value}))} className="mt-1 rounded-xl border-slate-200 text-sm" placeholder="https://example.com" /></Field>
                  <Field label="Icon / Emoji"><Input value={linkForm.icon} onChange={e=>setLinkForm(p=>({...p,icon:e.target.value}))} className="mt-1 rounded-xl border-slate-200 text-sm" placeholder="🔗" /></Field>
                  <Field label="Sort Order"><Input type="number" value={linkForm.sort_order} onChange={e=>setLinkForm(p=>({...p,sort_order:parseInt(e.target.value)||0}))} className="mt-1 rounded-xl border-slate-200 text-sm" /></Field>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={!!linkForm.is_active} onChange={e=>setLinkForm(p=>({...p,is_active:e.target.checked}))} className="h-4 w-4 rounded border-slate-300 text-emerald-600" />
                  Visible on public page
                </label>
                <div className="flex gap-2">
                  <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={saveLink} disabled={linkSaving}>{linkSaving?'Saving…':(editingLinkId?'Update':'Add Link')}</Button>
                  {editingLinkId && <Button type="button" variant="outline" className="border-slate-200" onClick={()=>{setEditingLinkId('');setLinkForm(EMPTY_LINK);}}>Cancel</Button>}
                </div>
              </div>
              {/* Link list */}
              {links.length > 0 ? (
                <div className="space-y-2">
                  {links.map(link => (
                    <div key={link.id} className="flex items-start justify-between gap-3 rounded-2xl border border-slate-100 bg-white p-3.5 shadow-sm">
                      <div className="flex items-start gap-2.5 min-w-0">
                        {link.icon && <span className="text-lg mt-0.5 shrink-0">{link.icon}</span>}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{link.title}</p>
                          <p className="text-xs text-slate-400 truncate">{link.url}</p>
                          <span className={`text-[10px] font-bold mt-1 inline-block ${link.is_active?'text-emerald-600':'text-slate-400'}`}>{link.is_active?'● Active':'○ Hidden'}</span>
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button type="button" size="icon" variant="outline" className="h-8 w-8 border-slate-200 rounded-xl" onClick={()=>editLink(link)}><Edit3 size={13}/></Button>
                        <Button type="button" size="icon" variant="outline" className="h-8 w-8 border-slate-200 text-red-600 hover:bg-red-50 rounded-xl" onClick={()=>deleteLink(link.id)}><Trash2 size={13}/></Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center rounded-2xl border border-dashed border-slate-200">
                  <Link2 size={32} className="mx-auto text-slate-200 mb-2"/>
                  <p className="text-sm text-slate-400">No links yet. Add your first one above.</p>
                </div>
              )}
            </>
          ) : (
            <div className="py-10 text-center rounded-2xl border border-dashed border-slate-200">
              <IdCard size={32} className="mx-auto text-slate-300 mb-3"/>
              <p className="text-sm text-slate-500 font-medium">Save your vCard first</p>
              <p className="text-xs text-slate-400 mt-1">Then add custom links here.</p>
            </div>
          )}
        </div>
      );

      /* ── DESIGN ───────────────────────────── */
      case 'design': return (
        <div className="space-y-8">

          {/* Template picker */}
          <div>
            <p className="text-sm font-bold text-slate-900 mb-1">Template</p>
            <p className="text-xs text-slate-500 mb-4">Choose the layout and style for your public page.</p>
            <div className="grid grid-cols-4 gap-3 sm:grid-cols-4">
              {TEMPLATES.map(tpl => (
                <TemplateThumbnail
                  key={tpl.key}
                  tpl={tpl}
                  selected={form.template === tpl.key}
                  onClick={() => set('template', tpl.key)}
                />
              ))}
            </div>
          </div>

          {/* Font family */}
          <div>
            <p className="text-sm font-bold text-slate-900 mb-3">Font Family</p>
            <div className="grid grid-cols-2 gap-2">
              {FONTS.map(f => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => set('font_family', f.key)}
                  className={`text-left rounded-2xl border-2 px-4 py-3 transition-all ${form.font_family === f.key ? 'border-emerald-600 bg-emerald-50 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <p className="text-sm font-bold text-slate-900" style={{ fontFamily: FONT_MAP[f.key] }}>{f.label}</p>
                  <p className="text-xs text-slate-400 mt-0.5" style={{ fontFamily: FONT_MAP[f.key] }}>{f.sample}</p>
                  {form.font_family === f.key && <CheckCircle2 size={13} className="text-emerald-600 mt-1" />}
                </button>
              ))}
            </div>
          </div>

          {/* Accent color */}
          <div>
            <p className="text-sm font-bold text-slate-900 mb-3">Accent Color</p>
            <div className="flex flex-wrap gap-3">
              {ACCENT_COLORS.map(c => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => set('theme_color', c.key)}
                  title={c.label}
                  className={`relative flex flex-col items-center gap-1 transition-all ${form.theme_color === c.key ? 'scale-110' : 'hover:scale-105'}`}
                >
                  <div className={`w-10 h-10 rounded-2xl shadow-md border-4 ${form.theme_color === c.key ? 'border-slate-900' : 'border-transparent'}`} style={{ background: c.hex }} />
                  <span className="text-[10px] font-semibold text-slate-500">{c.label}</span>
                  {form.theme_color === c.key && <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-slate-900 flex items-center justify-center"><CheckCircle2 size={10} className="text-white" /></div>}
                </button>
              ))}
            </div>
          </div>

          {/* Background */}
          <div>
            <p className="text-sm font-bold text-slate-900 mb-3">Background</p>
            <div className="rounded-2xl border border-slate-200 p-4 space-y-4">
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-2 block">Background Type</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'gradient', label: '🎨 Gradient', sub: 'Auto from theme' },
                    { key: 'solid',    label: '🟦 Solid',    sub: 'Single color' },
                    { key: 'image',    label: '🖼️ Image',    sub: 'Custom photo' },
                  ].map(opt => (
                    <button key={opt.key} type="button" onClick={() => set('bg_type', opt.key)}
                      className={`rounded-xl border-2 p-3 text-left transition-all ${form.bg_type === opt.key ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <p className="text-sm font-bold text-slate-900">{opt.label}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{opt.sub}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Solid presets */}
              {form.bg_type === 'solid' && (
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-2 block">Color Presets</Label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {BG_PRESETS.filter(p=>p.type==='solid').map(p => (
                      <button key={p.value} type="button" onClick={() => set('bg_value', p.value)}
                        title={p.label}
                        className={`w-10 h-10 rounded-xl border-2 shadow-sm transition-all ${form.bg_value===p.value?'border-slate-900 scale-110':'border-transparent hover:scale-105'}`}
                        style={{ background: p.value || '#ffffff' }}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="color" value={form.bg_value||'#f1f5f9'}
                      onChange={e=>set('bg_value',e.target.value)}
                      className="w-10 h-10 rounded-xl border border-slate-200 cursor-pointer" />
                    <div className="flex-1">
                      <Input value={form.bg_value} onChange={e=>set('bg_value',e.target.value)} placeholder="#f1f5f9 or any color" className="rounded-xl border-slate-200 text-sm" />
                    </div>
                  </div>
                </div>
              )}

              {/* Image URL */}
              {form.bg_type === 'image' && (
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Background Image URL</Label>
                  <Input value={form.bg_value} onChange={e=>set('bg_value',e.target.value)} placeholder="https://images.unsplash.com/…" className="rounded-xl border-slate-200 text-sm" />
                  <p className="text-xs text-slate-400 mt-1">Use a high-quality landscape image (1920×1080 or larger)</p>
                  {form.bg_value && (
                    <div className="mt-2 h-24 rounded-xl overflow-hidden border border-slate-200">
                      <img src={form.bg_value} alt="bg preview" className="h-full w-full object-cover" onError={e=>{e.target.style.display='none'}} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Button style */}
          <div>
            <p className="text-sm font-bold text-slate-900 mb-3">Button Style</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { key:'rounded',  label:'Rounded',  preview:'rounded-xl' },
                { key:'pill',     label:'Pill',      preview:'rounded-full' },
                { key:'minimal',  label:'Minimal',   preview:'rounded-md border border-current' },
                { key:'gradient', label:'Gradient',  preview:'rounded-xl' },
              ].map(s => (
                <button key={s.key} type="button" onClick={()=>set('button_style',s.key)}
                  className={`rounded-2xl border-2 py-3 px-2 transition-all ${form.button_style===s.key?'border-emerald-600 bg-emerald-50':'border-slate-200 hover:border-slate-300'}`}>
                  <div className={`h-7 flex items-center justify-center text-xs font-bold text-white mx-3 mb-2 bg-emerald-700 ${s.preview}`} style={s.key==='gradient'?{background:'linear-gradient(90deg,#047857,#10b981)'}:{}}>Btn</div>
                  <p className="text-xs font-semibold text-slate-700 text-center">{s.label}</p>
                  {form.button_style===s.key && <CheckCircle2 size={13} className="text-emerald-600 mx-auto mt-1" />}
                </button>
              ))}
            </div>
          </div>

          {/* JJuma branding toggle */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-slate-900">Show JJuma Branding</p>
              <p className="text-xs text-slate-500 mt-0.5">"Powered by JJuma Pay" footer on your page</p>
            </div>
            <Switch checked={!!form.show_jjuma_branding} onCheckedChange={v=>set('show_jjuma_branding',v)} disabled={!data?.access?.remove_branding_allowed} />
          </div>
          {!data?.access?.remove_branding_allowed && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">Upgrade your package to remove the branding footer.</p>
          )}

          {/* Status */}
          <div>
            <Label className="text-xs font-semibold text-slate-600">Profile Status</Label>
            <Select value={form.status} onValueChange={v=>set('status',v)}>
              <SelectTrigger className="mt-1 rounded-xl border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">✅ Active (public)</SelectItem>
                <SelectItem value="disabled">⏸ Disabled (hidden)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );

      /* ── SEO ──────────────────────────────── */
      case 'seo': return (
        <div className="space-y-4">
          <FInput label="SEO Title" helper="Shown in browser tab and Google" value={form.seo_title} onChange={e=>set('seo_title',e.target.value)} placeholder="Jane Doe | Kampala Creative" />
          <Field label="SEO Description" helper="Up to 160 characters">
            <Textarea value={form.seo_description} onChange={e=>set('seo_description',e.target.value)} className="mt-1 rounded-xl border-slate-200 text-sm resize-none" rows={3} placeholder="Short description for search engines…" />
          </Field>
          <FInput label="Keywords" helper="Comma-separated" value={form.seo_keywords} onChange={e=>set('seo_keywords',e.target.value)} placeholder="portfolio, design, kampala" />
          <FInput label="Social Share Image URL" helper="Shown when shared on WhatsApp, Twitter etc." value={form.social_share_image} onChange={e=>set('social_share_image',e.target.value)} placeholder="https://…" />
        </div>
      );

      default: return null;
    }
  };

  /* ══════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════ */
  return (
    <div className="space-y-6">

      {/* ── Page header ──────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            My Smart Link / vCard
          </h1>
          <p className="text-slate-500 text-sm mt-1">Your shareable public profile — choose a template, pick a font, and go live.</p>
        </div>
        {publicUrl && (
          <div className="flex items-center gap-2 shrink-0">
            <Button type="button" variant="outline" onClick={copyShareLink} className="border-slate-200" data-testid="copy-vcard-link-btn">
              <Copy size={15} className="mr-1.5" /> Copy
            </Button>
            <a href={publicUrl} target="_blank" rel="noreferrer">
              <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                <ExternalLink size={15} className="mr-1.5" /> Open Page
              </Button>
            </a>
          </div>
        )}
      </div>

      {/* ── Access warning ────────────────────────────────────── */}
      {data?.access && !data.access.can_create_vcard && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex gap-2.5">
          <AlertCircle size={16} className="mt-0.5 shrink-0 text-amber-600" />
          <span>Your package does not allow vCard creation. Upgrade to continue.</span>
        </div>
      )}

      {/* ── Public URL bar ───────────────────────────────────── */}
      {publicUrl && (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <AtSign size={15} className="text-emerald-600 shrink-0" />
            <p className="text-sm text-emerald-800 font-medium truncate">{publicUrl}</p>
          </div>
          <button onClick={copyShareLink} className="text-xs font-bold text-emerald-700 hover:underline shrink-0">Copy</button>
        </div>
      )}

      {/* ── Main grid ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-5">

        {/* Form panel */}
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          {/* Tabs */}
          <div className="border-b border-slate-100 px-1 pt-1">
            <div className="flex overflow-x-auto gap-0.5 scrollbar-none">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold whitespace-nowrap rounded-t-xl transition-all ${
                      activeTab === tab.key
                        ? 'bg-white text-emerald-700 border border-b-0 border-slate-200 -mb-px shadow-sm'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}>
                    <Icon size={14} /> {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <CardContent className="p-6">
            <form onSubmit={saveVcard}>
              {renderTab()}

              {/* Save + status buttons */}
              <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-3">
                <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white px-6" disabled={saving} data-testid="save-vcard-btn">
                  {saving ? 'Saving…' : (currentVcard ? '✓ Save Changes' : '+ Create vCard')}
                </Button>
                {currentVcard?.id && (
                  currentVcard.status === 'active'
                    ? <Button type="button" variant="outline" onClick={() => toggleStatus('disabled')} className="border-slate-200" data-testid="disable-vcard-btn">Disable</Button>
                    : <Button type="button" variant="outline" onClick={() => toggleStatus('active')} className="border-slate-200 text-emerald-700" data-testid="enable-vcard-btn">Enable</Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Live preview panel (sticky on desktop) */}
        <div className="space-y-4 xl:sticky xl:top-6 self-start">
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Eye size={15} className="text-slate-400" /> Live Preview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <LivePreview form={form} />
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-2.5 text-center">
                <p className="text-[10px] text-slate-400">Template: <span className="font-bold text-slate-600 capitalize">{form.template}</span> · Font: <span className="font-bold text-slate-600">{FONTS.find(f=>f.key===form.font_family)?.label||'Outfit'}</span></p>
              </div>
              {publicUrl && (
                <a href={publicUrl} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="w-full border-slate-200 text-sm">
                    <ExternalLink size={13} className="mr-1.5" /> View Full Page
                  </Button>
                </a>
              )}
            </CardContent>
          </Card>

          {/* Tips */}
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-4">
            <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide mb-2">💡 Design Tips</p>
            <ul className="space-y-1.5 text-xs text-emerald-700">
              <li>• <strong>Musician</strong> template looks best with a concert cover photo</li>
              <li>• <strong>Luxury</strong> gold template pairs with Playfair Display font</li>
              <li>• <strong>Creative</strong> glassmorphism shines on vivid gradient backgrounds</li>
              <li>• Use Unsplash.com for free high-quality background images</li>
              <li>• Keep your bio under 120 characters for best mobile display</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
